/**
 * Constants for the generic append-only `EventLogs` table, which records what happened to
 * an entity - who did it, why, and under which configuration.
 */
/**
 * Format is `<domain>.<entity>.<action>`, lowercase, dot-delimited, action in past tense.
 *
 * Past tense because these record things that happened, not commands. The `<domain>.`
 * prefix is what stops string soup as other features adopt this table - it gives each
 * area a namespace and makes `LIKE 'billing.%'` a useful query on its own.
 */
export var EVENT_TYPE;
(function (EVENT_TYPE) {
    EVENT_TYPE["BILLING_CANCELLATION_POLICY_APPLIED"] = "billing.cancellation_policy.applied";
    EVENT_TYPE["BILLING_CANCELLATION_POLICY_SKIPPED"] = "billing.cancellation_policy.skipped";
    EVENT_TYPE["BILLING_CANCELLATION_POLICY_REVERTED"] = "billing.cancellation_policy.reverted";
    EVENT_TYPE["BILLING_DNA_POLICY_APPLIED"] = "billing.dna_policy.applied";
    EVENT_TYPE["BOOKING_MANAGER_CHANGED"] = "booking.manager_changed";
    EVENT_TYPE["BOOKING_STATUS_CHANGED"] = "booking.status_changed";
    EVENT_TYPE["BOOKING_RESCHEDULED"] = "booking.rescheduled";
    EVENT_TYPE["BOOKING_SERVICE_CHANGED"] = "booking.service_changed";
    EVENT_TYPE["BOOKING_LOCATION_CHANGED"] = "booking.location_changed";
    EVENT_TYPE["BOOKING_ANY_MANAGER_AT_LOCATION_CHANGED"] = "booking.any_manager_at_location_changed";
    EVENT_TYPE["BOOKING_REMINDER_SENT"] = "booking.reminder_sent";
})(EVENT_TYPE || (EVENT_TYPE = {}));
/**
 * Who acted, not who it was for. A practice-initiated cancellation is still performed by
 * a manager clicking a button - the manager is the actor. CANCELLATION_INITIATOR
 * (PRACTICE / RESPONDENT) describes on whose behalf it happened and belongs in the event
 * payload instead. Collapsing the two loses that distinction.
 *
 * `actorId` points at a different table depending on this value (Managers, Admins,
 * Respondents, or nothing for SYSTEM/INTEGRATION), so EventLog carries no FK on it - a FK
 * cannot be expressed across a polymorphic reference. That is the accepted cost of one
 * generic table instead of a bespoke table per feature.
 */
export var ACTOR_TYPE;
(function (ACTOR_TYPE) {
    ACTOR_TYPE["MANAGER"] = "MANAGER";
    // Distinct from MANAGER: both are Manager rows, but "acted on by someone outside the
    // practice" is worth being able to filter on.
    ACTOR_TYPE["EXTERNAL_USER"] = "EXTERNAL_USER";
    ACTOR_TYPE["ADMIN"] = "ADMIN";
    ACTOR_TYPE["RESPONDENT"] = "RESPONDENT";
    ACTOR_TYPE["SYSTEM"] = "SYSTEM";
    ACTOR_TYPE["INTEGRATION"] = "INTEGRATION";
})(ACTOR_TYPE || (ACTOR_TYPE = {}));
/**
 * Real column, not a payload field. The DB is SQL Server, which has no native JSON type -
 * JSON lives in NVARCHAR(MAX) and cannot be indexed without a persisted computed column.
 * Anything that shows up in a WHERE clause has to be a real column or the query
 * table-scans, and "what was the outcome for this booking" is exactly that query.
 */
export var OUTCOME;
(function (OUTCOME) {
    OUTCOME["APPLIED"] = "APPLIED";
    OUTCOME["SKIPPED"] = "SKIPPED";
    OUTCOME["REVERTED"] = "REVERTED";
    OUTCOME["FAILED"] = "FAILED";
})(OUTCOME || (OUTCOME = {}));
/**
 * Why an operation produced the outcome it did, when `outcome` alone is not enough.
 *
 * Several paths in ChargeService delete charges and return with only a `logger.info`,
 * leaving no durable record of which branch ran. "Why was this booking not charged?" is the
 * dispute question, and a free-text log line does not answer it reliably.
 */
export var REASON_CODE;
(function (REASON_CODE) {
    REASON_CODE["OUTSIDE_PENALTY_PERIOD"] = "OUTSIDE_PENALTY_PERIOD";
    REASON_CODE["PRACTICE_INITIATED"] = "PRACTICE_INITIATED";
    REASON_CODE["NO_BOOKING_DATETIME"] = "NO_BOOKING_DATETIME";
})(REASON_CODE || (REASON_CODE = {}));
/**
 * Which config produced the policy applied to a booking.
 *
 * Company-specific settings currently overwrite the global ones in a local variable
 * inside ChargeService, so once that runs there is otherwise no way to tell which set was
 * used. This is the field that makes "which config won" reconstructible after the fact.
 */
export var POLICY_SOURCE;
(function (POLICY_SOURCE) {
    POLICY_SOURCE["GLOBAL"] = "GLOBAL";
    POLICY_SOURCE["COMPANY"] = "COMPANY";
})(POLICY_SOURCE || (POLICY_SOURCE = {}));
/**
 * A plain object rather than an enum so other domains can extend the set of subjects an
 * event can describe without touching this one.
 */
export const EVENT_SUBJECT_TYPE = {
    BOOKING: "Booking",
    INVOICE: "Invoice",
};
//# sourceMappingURL=eventLog.js.map